import * as React from 'react';
import { memo, useMemo, useCallback } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  LucideIcon,
  Hash,
  Calendar,
  BookOpen,
  Users,
  CalendarRange,
  Wallet,
  GraduationCap,
  UserRound,
  ClipboardList,
} from 'lucide-react';

import { IGroup, PublishedLmsType } from 'types';
import { PopupNames } from 'core';
import { usePopupStateOperations } from 'core/hooks';
import { PATHS } from 'router/config';

import { weekDays } from 'components/constants';
import { Switcher } from 'atoms';

import { GroupsTableComponent, IColumn, IRow } from './GroupsTable';

export interface IGroupsTable {
  data: IGroup[];
  loading?: boolean;
  error?: string;
  loadMore: () => void;
  hasMore: boolean;
}

const HeaderCell = ({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <Icon size={16} strokeWidth={2} />
    <span>{label}</span>
  </span>
);

/**
 * Header for the three narrow LMS-publication switch columns
 * (teacher / student / methodist). They used to be icon-only, which
 * read as three unlabelled glyphs — so we keep the icon but restore
 * the short letter beside it (`П/С/М` · `N/S/M`) and expose the full
 * meaning on hover via `title`. The letter carries the label; the
 * `title` carries the explanation.
 */
const SwitchHeaderCell = ({
  icon: Icon,
  letter,
  title,
}: {
  icon: LucideIcon;
  letter: string;
  title: string;
}) => (
  <span
    aria-label={title}
    title={title}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
  >
    <Icon size={16} strokeWidth={2} />
    <span>{letter}</span>
  </span>
);

const getColumnsSource = (t: TFunction): IColumn[] => [
  {
    Header: <HeaderCell icon={Hash} label={t('table.groups.number')} />,
    accessor: 'group',
    columnWidth: '208px',
  },
  {
    Header: <HeaderCell icon={Calendar} label={t('table.groups.startDate')} />,
    accessor: 'startDate',
    columnWidth: '96px',
    marginRight: '56px',
    marginLeft: '80px',
  },
  {
    Header: (
      <HeaderCell icon={BookOpen} label={t('table.groups.numberOfLessons')} />
    ),
    accessor: 'numberOfLessons',
    columnWidth: '80px',
  },
  {
    Header: (
      <HeaderCell icon={Users} label={t('table.groups.numberOfStudents')} />
    ),
    accessor: 'numberOfStudents',
    columnWidth: '88px',
    marginRight: '48px',
    marginLeft: '48px',
  },
  {
    Header: (
      <HeaderCell icon={CalendarRange} label={t('table.groups.lessonDays')} />
    ),
    accessor: 'lessonDays',
    columnWidth: '100px',
    marginRight: '48px',
  },
  {
    Header: (
      <HeaderCell icon={Wallet} label={t('table.groups.agreementsSum')} />
    ),
    accessor: 'agreementsSum',
    columnWidth: '142px',
    marginRight: '48px',
  },
];

/**
 * Local wrapper for the Switcher cells: the row itself is now
 * clickable (opens the group control center), so the switch click
 * has to stop propagation, otherwise toggling LMS publication would
 * also navigate. Stays inside the container — `Switcher` atom keeps
 * its event-neutral contract for other consumers.
 */
const SwitcherCell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span
    role="presentation"
    onClick={(e) => e.stopPropagation()}
    onKeyDown={(e) => e.stopPropagation()}
  >
    {children}
  </span>
);

export const GroupsTable = memo(
  ({ data, loading, error, loadMore, hasMore }: IGroupsTable) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();

    const columns: IColumn[] = useMemo(
      () => [
        ...getColumnsSource(t),
        {
          Header: (
            <SwitchHeaderCell
              icon={GraduationCap}
              letter={t('table.groups.teacher')}
              title={t('table.groups.teacherTitle')}
            />
          ),
          accessor: 'publishedLmsForTeacher',
          columnWidth: '46px',
          marginRight: '48px',
          Cell: ({ row }) => (
            <SwitcherCell>
              <Switcher
                checked={row.original.publishedLmsForTeacher}
                onClick={(checked) =>
                  checked
                    ? showPopup(PopupNames.addGroupInLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.teacher,
                      })
                    : showPopup(PopupNames.removeGroupFromLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.teacher,
                      })
                }
              />
            </SwitcherCell>
          ),
        },
        {
          Header: (
            <SwitchHeaderCell
              icon={UserRound}
              letter={t('table.groups.student')}
              title={t('table.groups.studentTitle')}
            />
          ),
          accessor: 'publishedLms',
          columnWidth: '46px',
          marginRight: '48px',
          Cell: ({ row }) => (
            <SwitcherCell>
              <Switcher
                checked={row.original.publishedLms}
                onClick={(checked) =>
                  checked
                    ? showPopup(PopupNames.addGroupInLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.student,
                      })
                    : showPopup(PopupNames.removeGroupFromLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.student,
                      })
                }
              />
            </SwitcherCell>
          ),
        },
        {
          Header: (
            <SwitchHeaderCell
              icon={ClipboardList}
              letter={t('table.groups.methodist')}
              title={t('table.groups.methodistTitle')}
            />
          ),
          accessor: 'publishedLmsForMethodist',
          columnWidth: '46px',
          marginRight: '56px',
          Cell: ({ row }) => (
            <SwitcherCell>
              <Switcher
                checked={row.original.publishedLmsForMethodist}
                onClick={(checked) =>
                  checked
                    ? showPopup(PopupNames.addGroupInLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.methodist,
                      })
                    : showPopup(PopupNames.removeGroupFromLMS, {
                        ...row.original,
                        publishedLmsType: PublishedLmsType.methodist,
                      })
                }
              />
            </SwitcherCell>
          ),
        },
      ],
      [showPopup, t],
    );
    const rows: IRow[] = useMemo(
      () =>
        data.map((debtor, idx) => ({
          ...debtor,
          num: idx + 1,
          lessonDays: debtor.lessonDays.map((num) => weekDays[num]).join(', '),
        })),
      [data],
    );

    const onEditButtonClick = useCallback(
      (groupId: string) => {
        showPopup(PopupNames.editGroupPopup, groupId);
      },
      [showPopup],
    );

    const onOpenDetail = useCallback(
      (groupId: string) => {
        history.push(PATHS.groupDetail.replace(':id', groupId));
      },
      [history],
    );

    if (error) {
      return <p>{error}</p>;
    }

    return (
      <GroupsTableComponent
        rows={rows}
        columns={columns}
        isFlexible
        noDataText={t('table.groups.noData')}
        loadMore={loadMore}
        hasMore={hasMore}
        onEditButtonClick={onEditButtonClick}
        onOpenDetail={onOpenDetail}
        editButtonLabel={t('table.groups.editAction')}
        loading={loading}
      />
    );
  },
);
